import { FC, ReactNode, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import Fab from "@mui/material/Fab";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NotificationsIcon from "@mui/icons-material/Notifications";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useUserStore } from "../../stores/user.store";
import { useActivitySidebar } from "../../hooks/useActivitySidebar";
import {
	useActivitySidebarUiStore,
	ACTIVITY_SIDEBAR_WIDTH,
	APP_BAR_HEIGHT,
} from "../../stores/activitySidebar.store";
import TodaysActivitiesList from "./TodaysActivitiesList";
import DelayedMissedList from "./DelayedMissedList";
import InputSubmittedList from "./InputSubmittedList";

interface SidebarCardProps {
	title: string;
	children: ReactNode;
}

// SIMS-7579 (UI redesign): each section is its own card in the rail (per Figma),
// replacing the previous single-panel-with-dividers layout. The three cards
// share the rail height equally (flex: 1) and each card's body scrolls
// internally so a long list (e.g. Delayed & Missed) doesn't grow the rail or
// crowd out the other cards.
const SidebarCard: FC<SidebarCardProps> = ({ title, children }) => (
	<Card
		sx={{
			borderRadius: 2,
			flex: "1 1 0",
			minHeight: 0,
			display: "flex",
			flexDirection: "column",
			overflow: "hidden",
		}}
	>
		<Typography variant="subtitle1" sx={{ fontWeight: 700, p: 2, pb: 1 }}>
			{title}
		</Typography>
		<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, pb: 2 }}>
			{children}
		</Box>
	</Card>
);

// SIMS-4146 + SIMS-7579(UI): the Activity Sidebar. Visible only when the
// coordinator is assigned to a Daily Workflow-enabled program (desktop/tablet).
// On wide screens it opens as a persistent rail that PUSHES the page content
// (the Layout reserves right-margin via the shared `pushing` flag); on narrower
// screens it falls back to a temporary overlay drawer so the page isn't crushed.
export const ActivitySidebar: FC = () => {
	const theme = useTheme();
	const isDesktopOrTablet = useMediaQuery(theme.breakpoints.up("sm"));
	// Push (persistent) only when there is room; overlay below this.
	const isWide = useMediaQuery(theme.breakpoints.up("lg"));
	const coordinatorId = useUserStore((state) => state.userInfo?.id);
	const { data } = useActivitySidebar(coordinatorId);
	const [open, setOpen] = useState(false);
	const setPushing = useActivitySidebarUiStore((state) => state.setPushing);

	const todaysActivities = data?.sections.todaysActivities.count ?? 0;
	const delayedMissed = data?.sections.delayedMissed.count ?? 0;
	// Defensive `?.` on the newest section: during a rollout the frontend may
	// briefly run against a backend that predates SIMS-4338 and omits it.
	const inputSubmitted = data?.sections.inputSubmitted?.count ?? 0;
	const total = todaysActivities + delayedMissed + inputSubmitted;

	const visible = isDesktopOrTablet && Boolean(data?.programsEnabled);
	// The Layout reserves space only when the rail is open in push mode.
	const pushing = visible && open && isWide;

	useEffect(() => {
		setPushing(pushing);
		return () => setPushing(false);
	}, [pushing, setPushing]);

	if (!visible) {
		return null;
	}

	return (
		<>
			{!open && (
				<Fab
					color="primary"
					size="medium"
					onClick={() => setOpen(true)}
					aria-label="Open activity sidebar"
					sx={{
						position: "fixed",
						right: 16,
						bottom: 24,
						zIndex: (t) => t.zIndex.drawer + 1,
					}}
				>
					<Badge badgeContent={total} color="error">
						<NotificationsIcon />
					</Badge>
				</Fab>
			)}
			<Drawer
				anchor="right"
				variant={isWide ? "persistent" : "temporary"}
				open={open}
				onClose={() => setOpen(false)}
				aria-labelledby="activity-sidebar-title"
				PaperProps={{
					sx: {
						width: ACTIVITY_SIDEBAR_WIDTH,
						boxSizing: "border-box",
						p: 2,
						// Sit below the fixed AppBar (like the left nav).
						top: `${APP_BAR_HEIGHT}px`,
						height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
						backgroundColor: theme.palette.surface.main,
						borderLeft: `1px solid ${theme.palette.divider}`,
						// Fixed-height flex column; the cards scroll, not the rail.
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					},
				}}
			>
				<Box
					sx={{
						flex: "none",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 1,
					}}
				>
					<Typography id="activity-sidebar-title" variant="h6">
						Activity
					</Typography>
					<IconButton
						onClick={() => setOpen(false)}
						aria-label="Collapse activity sidebar"
					>
						<ChevronRightIcon />
					</IconButton>
				</Box>

				{/* Equal-height cards that fill the remaining rail height. */}
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						gap: 2,
					}}
				>
					<SidebarCard title="Today's activities">
						<TodaysActivitiesList coordinatorId={coordinatorId} />
					</SidebarCard>
					<SidebarCard title="Input submitted">
						<InputSubmittedList coordinatorId={coordinatorId} />
					</SidebarCard>
					<SidebarCard title="Delayed & missed activities">
						<DelayedMissedList coordinatorId={coordinatorId} />
					</SidebarCard>
				</Box>
			</Drawer>
		</>
	);
};

export default ActivitySidebar;
